import { sortByName, sortAsc } from '../utils/sortOptions';
import { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ArrowRightIcon, CheckCircleIcon, ArrowPathIcon,
  ChevronDownIcon, ExclamationTriangleIcon, ArrowsRightLeftIcon,
  ClockIcon, MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const curYear  = () => { const y=new Date().getFullYear(); return `${y}-${y+1}`; };
const nextYear = () => { const y=new Date().getFullYear(); return `${y+1}-${y+2}`; };
const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-PK',{day:'2-digit',month:'short',year:'numeric'}) : '—';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .cp * { box-sizing:border-box; }
  .cp { font-family:'Sora',sans-serif; color:#1a1d2e; animation:cpFade .4s ease both; }
  @keyframes cpFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .cp-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.2rem; flex-wrap:wrap; gap:1rem; }
  .cp-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .cp-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  /* Tab switcher */
  .cp-tabs { display:flex; gap:4px; background:#f0f2f7; border-radius:12px; padding:4px; margin-bottom:1.4rem; width:fit-content; }
  .cp-tab  { display:flex; align-items:center; gap:7px; padding:8px 16px; border-radius:9px; border:none; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; color:#7880a4; background:transparent; transition:.15s; }
  .cp-tab svg { width:15px; height:15px; stroke-width:2; }
  .cp-tab.active { background:white; color:#1a1d2e; box-shadow:0 1px 4px rgba(0,0,0,.1); }
  /* Buttons */
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; } .btn-blue:hover  { background:#2d5ef0; }
  .btn-green { background:#10c98e; color:white; } .btn-green:hover { background:#0db87e; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; } .btn-red:hover { background:#ef4444; color:white; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-sm { padding:5px 10px; font-size:.73rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  /* Steps */
  .steps { display:flex; align-items:center; gap:0; margin-bottom:1.6rem; flex-wrap:wrap; }
  .step { display:flex; align-items:center; gap:8px; }
  .step-num { width:28px; height:28px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:.74rem; font-weight:700; flex-shrink:0; }
  .step-num.done    { background:#10c98e; color:white; }
  .step-num.active  { background:#3b6ef8; color:white; }
  .step-num.pending { background:#f0f2f7; color:#9094a8; }
  .step-label { font-size:.76rem; font-weight:600; }
  .step-label.active { color:#3b6ef8; } .step-label.pending{ color:#9094a8; } .step-label.done{ color:#10c98e; }
  .step-line { width:32px; height:2px; background:#e8ebf4; margin:0 4px; flex-shrink:0; }
  .step-line.done { background:#10c98e; }
  /* Panel */
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.4rem; margin-bottom:1.2rem; }
  .panel-title { font-size:.9rem; font-weight:700; color:#0f1123; margin-bottom:1rem; }
  .setup-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(180px,1fr)); gap:12px; margin-bottom:1rem; }
  .fl { display:flex; flex-direction:column; gap:4px; }
  .fl-label { font-size:.67rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .fl-select,.fl-input { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; outline:none; width:100%; }
  .fl-select:focus,.fl-input:focus { border-color:#3b6ef8; background:white; }
  .fl-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; outline:none; width:100%; resize:vertical; min-height:70px; }
  .fl-textarea:focus { border-color:#3b6ef8; background:white; }
  .arrow-row { display:flex; align-items:center; justify-content:center; gap:10px; padding:12px 0; flex-wrap:wrap; }
  .arrow-badge { display:flex; align-items:center; gap:6px; background:#f0f4ff; border:1.5px solid #c7d2fe; border-radius:10px; padding:8px 14px; font-size:.82rem; font-weight:600; color:#3b6ef8; }
  .arrow-icon { width:20px; height:20px; color:#9094a8; flex-shrink:0; }
  /* Student table */
  .stu-table { width:100%; border-collapse:collapse; }
  .stu-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .stu-table th { padding:10px 14px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .stu-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .stu-table tbody tr:last-child { border-bottom:none; }
  .stu-table tbody tr:hover { background:#fafbff; }
  .stu-table td { padding:10px 14px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .stu-name { font-weight:600; color:#0f1123; }
  .stu-meta { font-size:.7rem; color:#9094a8; margin-top:2px; }
  .pct-badge { font-family:'JetBrains Mono',monospace; font-size:.76rem; font-weight:700; padding:2px 8px; border-radius:99px; }
  .status-sel { display:flex; gap:6px; flex-wrap:wrap; }
  .status-btn { padding:4px 10px; border-radius:99px; border:1.5px solid; font-family:'Sora',sans-serif; font-size:.7rem; font-weight:600; cursor:pointer; transition:.15s; background:white; }
  .status-btn.promoted  { border-color:#3b6ef8; color:#3b6ef8; }
  .status-btn.promoted.on  { background:#3b6ef8; color:white; }
  .status-btn.held_back { border-color:#f59e0b; color:#f59e0b; }
  .status-btn.held_back.on { background:#f59e0b; color:white; }
  .override-sel { font-family:'Sora',sans-serif; font-size:.74rem; padding:5px 8px; border:1.5px solid #e0e4f0; border-radius:7px; background:white; outline:none; }
  .override-sel:focus { border-color:#3b6ef8; }
  .override-sel.custom { border-color:#8b5cf6; background:#f3f0ff; color:#8b5cf6; }
  .promoted-badge { font-size:.66rem; font-weight:700; padding:2px 8px; border-radius:99px; background:#ecfdf5; color:#10c98e; white-space:nowrap; }
  .bulk-bar { display:flex; gap:8px; align-items:center; flex-wrap:wrap; padding:10px 0; border-bottom:1px solid #f0f2f8; margin-bottom:10px; }
  .bulk-label { font-size:.74rem; color:#6b7194; font-weight:600; }
  .summary-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(140px,1fr)); gap:10px; margin-bottom:1.2rem; }
  .sum-card { background:#f8f9fc; border-radius:12px; border:1px solid #e8ebf4; padding:14px; text-align:center; }
  .sum-val  { font-size:1.5rem; font-weight:700; font-family:'JetBrains Mono',monospace; }
  .sum-label{ font-size:.68rem; color:#9094a8; font-weight:600; margin-top:3px; }
  .warning-box { background:#fffbeb; border:1px solid #fde68a; border-radius:10px; padding:12px 14px; font-size:.78rem; color:#92400e; margin-bottom:1rem; display:flex; gap:8px; }
  .warning-box svg { width:18px; height:18px; flex-shrink:0; margin-top:1px; }
  .info-box { background:#eff6ff; border:1px solid #bfdbfe; border-radius:10px; padding:12px 14px; font-size:.78rem; color:#1e40af; margin-bottom:1rem; display:flex; gap:8px; }
  .info-box svg { width:18px; height:18px; flex-shrink:0; margin-top:1px; }
  .cp-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .cp-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg)} }
  /* Transfer-specific */
  .stu-search { position:relative; }
  .stu-search input { width:100%; font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px 9px 36px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; outline:none; }
  .stu-search input:focus { border-color:#3b6ef8; background:white; }
  .stu-search svg { position:absolute; left:10px; top:50%; transform:translateY(-50%); width:15px; height:15px; color:#9094a8; stroke-width:2; pointer-events:none; }
  .stu-dropdown { position:absolute; top:calc(100% + 4px); left:0; right:0; background:white; border:1.5px solid #e0e4f0; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); z-index:100; max-height:220px; overflow-y:auto; }
  .stu-dropdown-item { padding:10px 14px; font-size:.82rem; cursor:pointer; border-bottom:1px solid #f3f5fb; }
  .stu-dropdown-item:last-child { border-bottom:none; }
  .stu-dropdown-item:hover { background:#f5f6fb; }
  .stu-dropdown-item .name { font-weight:600; color:#0f1123; }
  .stu-dropdown-item .meta { font-size:.7rem; color:#9094a8; }
  .selected-stu { display:flex; align-items:center; gap:12px; background:#f0f4ff; border:1.5px solid #c7d2fe; border-radius:10px; padding:12px 14px; margin-bottom:1rem; }
  .selected-stu .name { font-weight:700; color:#1e40af; }
  .selected-stu .meta { font-size:.72rem; color:#6b7194; margin-top:2px; }
  .selected-stu .clear { margin-left:auto; cursor:pointer; color:#9094a8; font-size:.72rem; font-weight:600; padding:3px 8px; border-radius:6px; border:1px solid #e0e4f0; background:white; }
  /* History table */
  .hist-table { width:100%; border-collapse:collapse; }
  .hist-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .hist-table th { padding:10px 14px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .hist-table tbody tr { border-bottom:1px solid #f3f5fb; }
  .hist-table tbody tr:last-child { border-bottom:none; }
  .hist-table td { padding:10px 14px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .transfer-arrow { display:inline-flex; align-items:center; gap:6px; }
  .transfer-from { color:#6b7194; }
  .transfer-to   { color:#3b6ef8; font-weight:600; }
  /* Mobile cards for stu-table / hist-table */
  .cp-promo-cards,.cp-xfer-cards,.cp-hist-cards { display:none; }
  .cp-mcard { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .cp-mc-top { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; margin-bottom:8px; flex-wrap:wrap; }
  .cp-mc-row { display:flex; align-items:center; justify-content:space-between; font-size:.76rem; padding:5px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .cp-mc-row:last-child { border-bottom:none; }
  .cp-mc-label { color:#9094a8; font-weight:600; white-space:nowrap; }
  @media (max-width:768px) {
    .cp-title{font-size:1.1rem;}
    .transfer-layout { grid-template-columns:1fr !important; }
    .setup-grid{grid-template-columns:1fr 1fr;}
    .stu-wrap,.hist-wrap { display:none !important; }
    .cp-promo-cards,.cp-xfer-cards,.cp-hist-cards { display:block; }
    .cp-tabs { width:100%; }
    .cp-tab { flex:1; justify-content:center; }
  }
`;

const pctColor = p => p>=80?'#10c98e':p>=60?'#f59e0b':'#ef4444';

// ─────────────────────────────────────────────────────────────────────────────
// PROMOTION TAB
// ─────────────────────────────────────────────────────────────────────────────
function PromotionTab({ classes: scopedClasses, readOnly }) {
  // Class Promotion is exempt from coordinator grade-scoping — every coordinator
  // can promote any class regardless of what they're normally assigned to.
  const [allClasses, setAllClasses] = useState(null);
  useEffect(() => {
    if (!readOnly) return;
    api.get('/coordinator/promotion/classes').then(r => setAllClasses(r.data||[])).catch(()=>{});
  }, [readOnly]);
  const classes = readOnly ? (allClasses||[]) : scopedClasses;

  const [step,        setStep]       = useState(1);
  const [fromClass,   setFromClass]  = useState('');
  const [fromSection, setFromSection]= useState('');
  const [fromYear,    setFromYear]   = useState(curYear());
  const [toClass,     setToClass]    = useState('');
  const [toSection,   setToSection]  = useState('');
  const [toYear,      setToYear]     = useState(nextYear());
  const [students,    setStudents]   = useState([]);
  const [loading,     setLoading]    = useState(false);
  const [executing,   setExecuting]  = useState(false);
  const [overrides,   setOverrides]  = useState({});
  const [result,      setResult]     = useState(null);
  // Minimum grade-report percentage needed to be promoted. Students below it (or with no
  // marks) start as "Held Back"; the admin can still promote any of them manually.
  const [passPct,     setPassPct]    = useState(() => { try { return localStorage.getItem('promotionPassPct') || '40'; } catch { return '40'; } });

  const fromSections = classes.find(c=>String(c._id)===String(fromClass))?.sections||[];
  const toSections   = classes.find(c=>String(c._id)===String(toClass))?.sections||[];

  const thresholdOk = () => { const n = Number(passPct); return passPct !== '' && !Number.isNaN(n) && n >= 0 && n <= 100; };
  const meetsCriteria = (pct) => pct != null && pct >= Number(passPct);

  const loadStudents = async () => {
    if (!fromClass||!fromSection||!fromYear) return toast.error('Select class, section and year');
    if (!thresholdOk()) return toast.error('Enter a pass percentage between 0 and 100');
    try { localStorage.setItem('promotionPassPct', String(passPct)); } catch { /* ignore */ }
    setLoading(true);
    try {
      const url = readOnly ? '/coordinator/promotion/preview' : '/admin/promotion/preview';
      const { data } = await api.get(`${url}?classId=${fromClass}&section=${fromSection}&academicYear=${fromYear}`);
      setStudents(data.students||[]);
      const init = {};
      (data.students||[]).forEach(s => {
        init[s.studentId] = { toClass: toClass||fromClass, toSection: toSection||fromSection, status: meetsCriteria(s.percentage) ? 'promoted' : 'held_back', note: '' };
      });
      setOverrides(init);
      setStep(2);
    } catch { toast.error('Failed to load students'); }
    finally { setLoading(false); }
  };

  const applyBulkTarget = () => {
    if (!toClass||!toSection) return toast.error('Select target class and section first');
    setOverrides(p => {
      const u={...p};
      Object.keys(u).forEach(sid=>{ if(u[sid].status!=='held_back') u[sid]={...u[sid],toClass,toSection}; });
      return u;
    });
    toast.success('Bulk target applied');
  };

  const setOv = (sid,field,val) => setOverrides(p=>({...p,[sid]:{...p[sid],[field]:val}}));
  const applyCriteria = () => {
    if (!thresholdOk()) return toast.error('Enter a pass percentage between 0 and 100');
    try { localStorage.setItem('promotionPassPct', String(passPct)); } catch { /* ignore */ }
    setOverrides(p => {
      const u = { ...p };
      students.filter(s => !s.alreadyPromoted).forEach(s => {
        u[s.studentId] = { ...u[s.studentId], status: meetsCriteria(s.percentage) ? 'promoted' : 'held_back' };
      });
      return u;
    });
    toast.success(`Applied: ${passPct}% and above promoted, the rest held back`);
  };
  const setAllStatus = (status) => setOverrides(p=>{const u={...p};Object.keys(u).forEach(sid=>{u[sid]={...u[sid],status}});return u;});

  const buildPromotions = () => students
    .filter(s=>!s.alreadyPromoted)
    .map(s=>{
      const ov=overrides[s.studentId]||{};
      const status=ov.status||'promoted';
      return { studentId:s.studentId, toClass:status==='held_back'?fromClass:(ov.toClass||toClass||fromClass), toSection:status==='held_back'?fromSection:(ov.toSection||toSection||fromSection), status, note:ov.note||'' };
    });

  const handleExecute = async () => {
    const promotions=buildPromotions();
    if (!promotions.length) return toast.error('No students to promote');
    if (!toYear) return toast.error('Set target academic year');
    setExecuting(true);
    try {
      if (readOnly) {
        await api.post('/coordinator/requests',{ type:'promotion_execute', payload:{ fromClass,fromSection,fromYear,toYear,promotions, fromClassName:classes.find(c=>String(c._id)===String(fromClass))?.name||'', toClassName:classes.find(c=>String(c._id)===String(toClass))?.name||'' }});
        setResult({ message:`Promotion request submitted — ${promotions.filter(p=>p.status==='promoted').length} to promote, ${promotions.filter(p=>p.status==='held_back').length} held back.`, results:{promoted:promotions.filter(p=>p.status==='promoted').length,heldBack:promotions.filter(p=>p.status==='held_back').length,skipped:0}});
        toast.success('Promotion request submitted');
      } else {
        const { data } = await api.post('/admin/promotion/execute',{ fromClass,fromSection,fromYear,toYear,promotions });
        setResult(data);
        toast.success(data.message);
      }
      setStep(3);
    } catch(err){ toast.error(err.response?.data?.message||'Failed'); }
    finally{ setExecuting(false); }
  };

  const reset = () => { setStep(1);setStudents([]);setOverrides({});setResult(null);setFromClass('');setFromSection('');setToClass('');setToSection('');setFromYear(curYear());setToYear(nextYear()); };
  const promoted=students.filter(s=>!s.alreadyPromoted&&overrides[s.studentId]?.status!=='held_back');
  const heldBack=students.filter(s=>!s.alreadyPromoted&&overrides[s.studentId]?.status==='held_back');
  const already=students.filter(s=>s.alreadyPromoted);
  const fromCls=classes.find(c=>String(c._id)===String(fromClass));
  const toCls=classes.find(c=>String(c._id)===String(toClass));

  return (
    <>
      <div className="cp-hdr">
        <div><div className="cp-title">Class Promotion</div><div className="cp-sub">Promote students to next class or hold back for the new academic year</div></div>
        {step>1&&<button className="btn btn-ghost btn-sm" onClick={reset}><ArrowPathIcon/>Start Over</button>}
      </div>
      <div className="steps">
        {[{n:1,l:'Setup'},{n:2,l:'Review & Override'},{n:3,l:'Done'}].map((s,i)=>(
          <div key={s.n} style={{display:'flex',alignItems:'center'}}>
            <div className="step">
              <div className={`step-num ${step>s.n?'done':step===s.n?'active':'pending'}`}>{step>s.n?<CheckCircleIcon style={{width:14,height:14}}/>:s.n}</div>
              <span className={`step-label ${step>s.n?'done':step===s.n?'active':'pending'}`}>{s.l}</span>
            </div>
            {i<2&&<div className={`step-line ${step>s.n?'done':''}`}/>}
          </div>
        ))}
      </div>
      {step===1&&(
        <div className="panel">
          <div className="panel-title">Configure Promotion</div>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:'.76rem',fontWeight:700,color:'#6b7194',marginBottom:8}}>FROM (current)</div>
            <div className="setup-grid">
              <div className="fl"><label className="fl-label">Class</label>
                <select className="fl-select" value={fromClass} onChange={e=>{setFromClass(e.target.value);setFromSection('');}}>
                  <option value="">Select class…</option>
                  {sortByName(classes).map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div className="fl"><label className="fl-label">Section</label>
                <select className="fl-select" value={fromSection} onChange={e=>setFromSection(e.target.value)} disabled={!fromClass}>
                  <option value="">Select section…</option>
                  {sortAsc(fromSections).map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="fl"><label className="fl-label">Academic Year</label>
                <input className="fl-input" value={fromYear} onChange={e=>setFromYear(e.target.value)} placeholder="2024-2025"/>
              </div>
            </div>
          </div>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:'.76rem',fontWeight:700,color:'#6b7194',marginBottom:8}}>TO (default target)</div>
            <div className="setup-grid">
              <div className="fl"><label className="fl-label">Target Class</label>
                <select className="fl-select" value={toClass} onChange={e=>{setToClass(e.target.value);setToSection('');}}>
                  <option value="">Select class…</option>
                  {classes.map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div className="fl"><label className="fl-label">Target Section</label>
                <select className="fl-select" value={toSection} onChange={e=>setToSection(e.target.value)} disabled={!toClass}>
                  <option value="">Select section…</option>
                  {(classes.find(c=>String(c._id)===String(toClass))?.sections||[]).map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="fl"><label className="fl-label">Target Academic Year</label>
                <input className="fl-input" value={toYear} onChange={e=>setToYear(e.target.value)} placeholder="2025-2026"/>
              </div>
            </div>
          </div>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:'.76rem',fontWeight:700,color:'#6b7194',marginBottom:8}}>PROMOTION CRITERIA</div>
            <div className="setup-grid">
              <div className="fl"><label className="fl-label">Pass Percentage (from grade reports)</label>
                <input className="fl-input" type="number" min="0" max="100" value={passPct} onChange={e=>setPassPct(e.target.value)} placeholder="e.g. 40"/>
              </div>
            </div>
            <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:6}}>Students at or above this percentage are promoted automatically; the rest are held back. You can still promote any held-back student manually on the next step.</div>
          </div>
          {fromClass&&fromSection&&toClass&&toSection&&(
            <div className="arrow-row">
              <div className="arrow-badge">{fromCls?.name} - {fromSection}<br/><span style={{fontSize:'.66rem',color:'#9094a8'}}>{fromYear}</span></div>
              <ArrowRightIcon className="arrow-icon"/>
              <div className="arrow-badge" style={{background:'#f0fdf4',borderColor:'#a7f3d0',color:'#065f46'}}>{toCls?.name} - {toSection}<br/><span style={{fontSize:'.66rem',color:'#9094a8'}}>{toYear}</span></div>
            </div>
          )}
          <button className="btn btn-blue" onClick={loadStudents} disabled={loading||!fromClass||!fromSection||!fromYear}>
            {loading?<><div className="spinner"/>Loading…</>:<>Load Students<ArrowRightIcon style={{width:15,height:15}}/></>}
          </button>
        </div>
      )}
      {step===2&&(
        <>
          {students.length===0?<div className="panel"><div className="cp-empty">No active students found in {fromCls?.name} - {fromSection}</div></div>:(
            <div className="panel">
              <div className="panel-title">Review Students — {fromCls?.name} - {fromSection} ({fromYear})</div>
              <div className="bulk-bar">
                <span className="bulk-label">Pass %:</span>
                <input className="fl-input" type="number" min="0" max="100" value={passPct} onChange={e=>setPassPct(e.target.value)} style={{width:70,padding:'5px 8px'}}/>
                <button className="btn btn-blue btn-sm" onClick={applyCriteria}>Apply Criteria</button>
                <span className="bulk-label" style={{marginLeft:10}}>Bulk:</span>
                <button className="btn btn-ghost btn-sm" onClick={()=>setAllStatus('promoted')}>✓ Mark All Promoted</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>setAllStatus('held_back')}>⚠ Mark All Held Back</button>
                <button className="btn btn-ghost btn-sm" onClick={applyBulkTarget}>Apply Target to All</button>
              </div>
              {already.length>0&&<div className="warning-box"><ExclamationTriangleIcon/><span>{already.length} student{already.length!==1?'s':''} already promoted this year — they will be skipped.</span></div>}
              <div className="stu-wrap"><table className="stu-table">
                <thead><tr><th>#</th><th>Student</th><th>Performance</th><th>Status</th><th>Target Class</th><th>Target Section</th></tr></thead>
                <tbody>
                  {students.map((s,i)=>{
                    const ov=overrides[s.studentId]||{};
                    const isHeld=ov.status==='held_back';
                    const ovClass=ov.toClass||toClass||fromClass;
                    const ovSec=ov.toSection||toSection||fromSection;
                    const ovClsSections=classes.find(c=>String(c._id)===String(ovClass))?.sections||[];
                    if(s.alreadyPromoted) return(
                      <tr key={s.studentId} style={{opacity:.5}}>
                        <td style={{color:'#9094a8'}}>{i+1}</td>
                        <td><div className="stu-name">{s.name}</div><div className="stu-meta">Roll: {s.rollNo} · {s.admissionNo}</div></td>
                        <td><span className="pct-badge" style={{background:'#f0f2f7',color:'#9094a8'}}>{s.percentage!=null?`${s.percentage}%`:'—'}</span></td>
                        <td><span className="promoted-badge">✓ Already Promoted → {s.promotedTo}</span></td>
                        <td colSpan={2}/>
                      </tr>
                    );
                    return(
                      <tr key={s.studentId}>
                        <td style={{color:'#9094a8',fontSize:'.74rem'}}>{i+1}</td>
                        <td><div className="stu-name">{s.name}</div><div className="stu-meta">Roll: {s.rollNo} · {s.admissionNo}</div></td>
                        <td>{s.percentage!=null?<span className="pct-badge" style={{background:`${pctColor(s.percentage)}18`,color:pctColor(s.percentage)}}>{s.percentage}%</span>:<span style={{color:'#c0c5dc',fontSize:'.74rem'}}>No data</span>}{!isHeld&&!meetsCriteria(s.percentage)&&<div style={{fontSize:'.66rem',color:'#f59e0b',fontWeight:600,marginTop:3}}>Below {passPct}% — promoted manually</div>}</td>
                        <td>
                          <div className="status-sel">
                            <button className={`status-btn promoted${!isHeld?' on':''}`} onClick={()=>setOv(s.studentId,'status','promoted')}>Promote</button>
                            <button className={`status-btn held_back${isHeld?' on':''}`} onClick={()=>setOv(s.studentId,'status','held_back')}>Hold Back</button>
                          </div>
                        </td>
                        <td>{isHeld?<span style={{fontSize:'.74rem',color:'#f59e0b',fontWeight:600}}>Same class</span>:<select className={`override-sel ${ovClass!==toClass?'custom':''}`} value={ovClass} onChange={e=>{setOv(s.studentId,'toClass',e.target.value);setOv(s.studentId,'toSection','');}}>{classes.map(c=><option key={c._id} value={c._id}>{c.name}</option>)}</select>}</td>
                        <td>{isHeld?<span style={{fontSize:'.74rem',color:'#f59e0b',fontWeight:600}}>Same section</span>:<select className={`override-sel ${ovSec!==toSection?'custom':''}`} value={ovSec} onChange={e=>setOv(s.studentId,'toSection',e.target.value)}>{ovClsSections.map(sec=><option key={sec} value={sec}>{sec}</option>)}</select>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table></div>

              <div className="cp-promo-cards">
                {students.map((s,i)=>{
                  const ov=overrides[s.studentId]||{};
                  const isHeld=ov.status==='held_back';
                  const ovClass=ov.toClass||toClass||fromClass;
                  const ovSec=ov.toSection||toSection||fromSection;
                  const ovClsSections=classes.find(c=>String(c._id)===String(ovClass))?.sections||[];
                  if(s.alreadyPromoted) return(
                    <div key={s.studentId} className="cp-mcard" style={{opacity:.6}}>
                      <div className="cp-mc-top">
                        <div><div className="stu-name">#{i+1} · {s.name}</div><div className="stu-meta">Roll: {s.rollNo} · {s.admissionNo}</div></div>
                        <span className="pct-badge" style={{background:'#f0f2f7',color:'#9094a8'}}>{s.percentage!=null?`${s.percentage}%`:'—'}</span>
                      </div>
                      <span className="promoted-badge">✓ Already Promoted → {s.promotedTo}</span>
                    </div>
                  );
                  return(
                    <div key={s.studentId} className="cp-mcard">
                      <div className="cp-mc-top">
                        <div><div className="stu-name">#{i+1} · {s.name}</div><div className="stu-meta">Roll: {s.rollNo} · {s.admissionNo}</div></div>
                        {s.percentage!=null?<span className="pct-badge" style={{background:`${pctColor(s.percentage)}18`,color:pctColor(s.percentage)}}>{s.percentage}%</span>:<span style={{color:'#c0c5dc',fontSize:'.74rem'}}>No data</span>}
                        {!isHeld&&!meetsCriteria(s.percentage)&&<div style={{fontSize:'.66rem',color:'#f59e0b',fontWeight:600,marginTop:3}}>Below {passPct}% — promoted manually</div>}
                      </div>
                      <div className="cp-mc-row">
                        <span className="cp-mc-label">Status</span>
                        <div className="status-sel">
                          <button className={`status-btn promoted${!isHeld?' on':''}`} onClick={()=>setOv(s.studentId,'status','promoted')}>Promote</button>
                          <button className={`status-btn held_back${isHeld?' on':''}`} onClick={()=>setOv(s.studentId,'status','held_back')}>Hold Back</button>
                        </div>
                      </div>
                      <div className="cp-mc-row">
                        <span className="cp-mc-label">Target Class</span>
                        {isHeld?<span style={{fontSize:'.74rem',color:'#f59e0b',fontWeight:600}}>Same class</span>:<select className={`override-sel ${ovClass!==toClass?'custom':''}`} value={ovClass} onChange={e=>{setOv(s.studentId,'toClass',e.target.value);setOv(s.studentId,'toSection','');}}>{classes.map(c=><option key={c._id} value={c._id}>{c.name}</option>)}</select>}
                      </div>
                      <div className="cp-mc-row">
                        <span className="cp-mc-label">Target Section</span>
                        {isHeld?<span style={{fontSize:'.74rem',color:'#f59e0b',fontWeight:600}}>Same section</span>:<select className={`override-sel ${ovSec!==toSection?'custom':''}`} value={ovSec} onChange={e=>setOv(s.studentId,'toSection',e.target.value)}>{ovClsSections.map(sec=><option key={sec} value={sec}>{sec}</option>)}</select>}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{marginTop:16,paddingTop:16,borderTop:'1px solid #f0f2f8'}}>
                <div style={{fontSize:'.76rem',fontWeight:700,color:'#6b7194',marginBottom:10}}>Summary</div>
                <div className="summary-grid">
                  <div className="sum-card"><div className="sum-val" style={{color:'#3b6ef8'}}>{promoted.length}</div><div className="sum-label">To Promote</div></div>
                  <div className="sum-card"><div className="sum-val" style={{color:'#f59e0b'}}>{heldBack.length}</div><div className="sum-label">Held Back</div></div>
                  <div className="sum-card"><div className="sum-val" style={{color:'#9094a8'}}>{already.length}</div><div className="sum-label">Already Done</div></div>
                  <div className="sum-card"><div className="sum-val" style={{color:'#0f1123'}}>{students.length}</div><div className="sum-label">Total</div></div>
                </div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  <button className="btn btn-ghost" onClick={()=>setStep(1)}>← Back</button>
                  <button className="btn btn-green" onClick={handleExecute} disabled={executing||(!promoted.length&&!heldBack.length)}>
                    {executing?<><div className="spinner"/>Processing…</>:readOnly?<><CheckCircleIcon/>Request Promotion</>:<><CheckCircleIcon/>Confirm Promotion</>}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {step===3&&result&&(
        <div className="panel" style={{textAlign:'center',padding:'2.5rem'}}>
          <CheckCircleIcon style={{width:48,height:48,color:'#10c98e',margin:'0 auto 16px',display:'block'}}/>
          <div style={{fontSize:'1.1rem',fontWeight:700,color:'#0f1123',marginBottom:6}}>{readOnly?'Request Submitted!':'Promotion Complete!'}</div>
          <div style={{fontSize:'.8rem',color:'#9094a8',marginBottom:20}}>{result.message}</div>
          <div className="summary-grid" style={{maxWidth:400,margin:'0 auto 20px'}}>
            <div className="sum-card"><div className="sum-val" style={{color:'#10c98e'}}>{result.results?.promoted||0}</div><div className="sum-label">Promoted</div></div>
            <div className="sum-card"><div className="sum-val" style={{color:'#f59e0b'}}>{result.results?.heldBack||0}</div><div className="sum-label">Held Back</div></div>
            <div className="sum-card"><div className="sum-val" style={{color:'#9094a8'}}>{result.results?.skipped||0}</div><div className="sum-label">Skipped</div></div>
          </div>
          {result.results?.errors?.length>0&&<div className="warning-box" style={{textAlign:'left',maxWidth:400,margin:'0 auto 16px'}}><ExclamationTriangleIcon/><span>{result.results.errors.length} error(s) occurred.</span></div>}
          <button className="btn btn-blue" onClick={reset}><ArrowPathIcon/>Promote Another Class</button>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION TRANSFER TAB
// ─────────────────────────────────────────────────────────────────────────────
function SectionTransferTab({ classes, readOnly }) {
  const [activeView,  setActiveView]  = useState('browse');   // 'browse' | 'history'
  const [selClass,    setSelClass]    = useState(null);       // selected class object
  const [selSection,  setSelSection]  = useState(null);       // selected section string
  const [students,    setStudents]    = useState([]);
  const [loadingStu,  setLoadingStu]  = useState(false);
  const [selStudent,  setSelStudent]  = useState(null);       // student to transfer

  // Transfer form
  const [toClassId,   setToClassId]   = useState('');
  const [toSection,   setToSection]   = useState('');
  const [reason,      setReason]      = useState('');
  const [academicYear,setAcademicYear]= useState(() => { const y=new Date().getFullYear(); return `${y}-${y+1}`; });
  const [executing,   setExecuting]   = useState(false);
  const [done,        setDone]        = useState(null);

  // History
  const [history,        setHistory]      = useState([]);
  const [loadingHistory, setLoadingHistory]= useState(false);
  const [histSearch,     setHistSearch]   = useState('');

  const toSections = classes.find(c=>String(c._id)===String(toClassId))?.sections||[];

  // Load students when section selected
  useEffect(() => {
    if (!selClass || !selSection) { setStudents([]); return; }
    setLoadingStu(true);
    const url = readOnly ? '/coordinator/students' : '/admin/students';
    api.get(`${url}?classId=${selClass._id}&section=${selSection}`)
      .then(r => {
        const arr = Array.isArray(r.data) ? r.data : (r.data.students||[]);
        setStudents(arr.filter(s => s.isActive !== false));
      })
      .catch(() => setStudents([]))
      .finally(() => setLoadingStu(false));
  }, [selClass, selSection, readOnly]);

  const selectStudent = (s) => {
    setSelStudent(s);
    setToClassId(s.class?._id || s.class || selClass?._id || '');
    setToSection(s.section || selSection || '');
    setDone(null);
  };

  const clearTransfer = () => {
    setSelStudent(null);
    setToClassId('');
    setToSection('');
    setReason('');
    setDone(null);
  };

  const resetAll = () => {
    setSelClass(null);
    setSelSection(null);
    setStudents([]);
    clearTransfer();
  };

  const handleTransfer = async () => {
    if (!selStudent) return toast.error('Select a student first');
    if (!toClassId || !toSection) return toast.error('Select target class and section');
    const fromClassId = selStudent.class?._id || selStudent.class;
    const fromSection = selStudent.section;
    if (String(fromClassId)===String(toClassId) && fromSection===toSection) {
      return toast.error('Student is already in this section');
    }
    if (!reason.trim()) return toast.error('Reason is required');
    setExecuting(true);
    try {
      if (readOnly) {
        await api.post('/coordinator/request/section-transfer', {
          studentId:   selStudent._id,
          toClassId,
          toSection,
          reason,
          academicYear,
          studentName: selStudent.user?.name || selStudent.name,
          fromClass:   fromClassId,
          fromSection,
        });
        setDone({ message: 'Section transfer request submitted. Admin will review it.', isRequest: true });
        toast.success('Request submitted');
      } else {
        const { data } = await api.post('/admin/section-transfer', {
          studentId: selStudent._id,
          toClassId,
          toSection,
          reason,
          academicYear,
        });
        setDone({ message: data.message, attendanceMigrated: data.attendanceMigrated, feesMigrated: data.feesMigrated, isRequest: false });
        toast.success('Transfer complete');
        // Refresh student list
        setStudents(p => p.filter(s => s._id !== selStudent._id));
        setSelStudent(null);
        setToClassId('');
        setToSection('');
        setReason('');
      }
    } catch(err) { toast.error(err.response?.data?.message||'Transfer failed'); }
    finally { setExecuting(false); }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const url = readOnly ? '/coordinator/section-transfer/history' : '/admin/section-transfer/history';
      const { data } = await api.get(url);
      setHistory(data.transfers || data || []);
    } catch { toast.error('Failed to load history'); }
    finally { setLoadingHistory(false); }
  };

  useEffect(() => { if (activeView==='history') loadHistory(); }, [activeView]);

  const filteredHistory = history.filter(t => {
    if (!histSearch) return true;
    const s = histSearch.toLowerCase();
    return (
      t.student?.user?.name?.toLowerCase().includes(s) ||
      t.student?.admissionNo?.toLowerCase().includes(s) ||
      t.fromClass?.name?.toLowerCase().includes(s) ||
      t.toClass?.name?.toLowerCase().includes(s)
    );
  });

  const fromCls = classes.find(c=>c._id===(selStudent?.class?._id||selStudent?.class));
  const toCls   = classes.find(c=>String(c._id)===String(toClassId));
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-PK',{day:'2-digit',month:'short',year:'numeric'}) : '—';

  return (
    <>
      <div className="cp-hdr">
        <div>
          <div className="cp-title">Mid-Session Section Transfer</div>
          <div className="cp-sub">Move a student to a different section within the same academic session</div>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className={`btn btn-sm ${activeView==='browse'?'btn-blue':'btn-ghost'}`} onClick={()=>setActiveView('browse')}><ArrowsRightLeftIcon/>Transfer</button>
          <button className={`btn btn-sm ${activeView==='history'?'btn-blue':'btn-ghost'}`} onClick={()=>{setActiveView('history');}}><ClockIcon/>History</button>
        </div>
      </div>

      {activeView==='browse' && (
        <>
          <div className="info-box">
            <ExclamationTriangleIcon style={{color:'#3b82f6'}}/>
            <div>
              <strong>What happens on transfer:</strong> Student's section updated immediately.
              Attendance records for the <strong>current month</strong> migrate to new section.
              Unpaid fee bills for the <strong>current month</strong> updated to new section.
              Past records stay as-is.
            </div>
          </div>

          <div className="transfer-layout" style={{display:'grid',gridTemplateColumns:'260px 1fr',gap:16,alignItems:'start'}}>

            {/* ── Left: Class + Section picker ── */}
            <div>
              <div className="panel" style={{padding:'14px'}}>
                <div className="panel-title" style={{marginBottom:10}}>Select Class</div>
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {classes.map(cls=>(
                    <button key={cls._id}
                      onClick={()=>{ setSelClass(cls); setSelSection(null); setStudents([]); clearTransfer(); }}
                      style={{
                        display:'flex',alignItems:'center',justifyContent:'space-between',
                        padding:'9px 12px',borderRadius:9,border:'1.5px solid',
                        fontFamily:'Sora',fontSize:'.82rem',fontWeight:600,cursor:'pointer',
                        textAlign:'left',transition:'.15s',
                        borderColor: selClass?._id===cls._id ? '#3b6ef8' : '#e0e4f0',
                        background:  selClass?._id===cls._id ? '#eef2ff' : 'white',
                        color:       selClass?._id===cls._id ? '#3b6ef8' : '#0f1123',
                      }}>
                      <span>{cls.name}</span>
                    </button>
                  ))}
                </div>

                {selClass && (
                  <>
                    <div style={{fontSize:'.68rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.07em',margin:'14px 0 6px'}}>Section</div>
                    <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                      {(selClass.sections||[]).map(sec=>(
                        <button key={sec}
                          onClick={()=>{ setSelSection(sec); clearTransfer(); }}
                          style={{
                            padding:'6px 14px',borderRadius:99,border:'1.5px solid',
                            fontFamily:'Sora',fontSize:'.8rem',fontWeight:700,cursor:'pointer',transition:'.15s',
                            borderColor: selSection===sec ? '#3b6ef8' : '#e0e4f0',
                            background:  selSection===sec ? '#3b6ef8' : 'white',
                            color:       selSection===sec ? 'white' : '#6b7194',
                          }}>
                          {sec}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ── Right: Student list + Transfer form ── */}
            <div>
              {!selClass && (
                <div className="panel">
                  <div className="cp-empty" style={{padding:'2rem'}}>
                    ← Select a class to see students
                  </div>
                </div>
              )}

              {selClass && !selSection && (
                <div className="panel">
                  <div className="cp-empty" style={{padding:'2rem'}}>
                    Select a section from the left
                  </div>
                </div>
              )}

              {selClass && selSection && !selStudent && (
                <div className="panel">
                  <div className="panel-title" style={{marginBottom:12}}>
                    {selClass.name} — Section {selSection}
                    <span style={{fontWeight:500,color:'#9094a8',fontSize:'.76rem',marginLeft:8}}>
                      {loadingStu ? 'Loading…' : `${students.length} students`}
                    </span>
                  </div>
                  {loadingStu ? (
                    <div className="cp-loading"><div className="spinner"/>Loading students…</div>
                  ) : students.length===0 ? (
                    <div className="cp-empty">No active students in this section</div>
                  ) : (
                    <>
                      <div className="stu-wrap">
                        <table className="stu-table">
                          <thead><tr>
                            <th>#</th><th>Student</th><th>Roll No</th><th>Admission No</th><th>Action</th>
                          </tr></thead>
                          <tbody>
                            {students.map((s,i)=>(
                              <tr key={s._id}>
                                <td style={{color:'#9094a8',fontSize:'.74rem'}}>{i+1}</td>
                                <td><div className="stu-name">{s.user?.name||s.name||'—'}</div></td>
                                <td><span style={{fontFamily:'JetBrains Mono',fontSize:'.78rem'}}>{s.rollNo}</span></td>
                                <td><span style={{fontFamily:'JetBrains Mono',fontSize:'.78rem',color:'#6b7194'}}>{s.admissionNo}</span></td>
                                <td>
                                  <button className="btn btn-sm btn-blue" onClick={()=>selectStudent(s)}>
                                    <ArrowsRightLeftIcon/>Transfer
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="cp-xfer-cards">
                        {students.map((s,i)=>(
                          <div key={s._id} className="cp-mcard">
                            <div className="cp-mc-top">
                              <div className="stu-name">#{i+1} · {s.user?.name||s.name||'—'}</div>
                            </div>
                            <div className="cp-mc-row"><span className="cp-mc-label">Roll No</span><span style={{fontFamily:'JetBrains Mono',fontSize:'.78rem'}}>{s.rollNo}</span></div>
                            <div className="cp-mc-row"><span className="cp-mc-label">Admission No</span><span style={{fontFamily:'JetBrains Mono',fontSize:'.78rem',color:'#6b7194'}}>{s.admissionNo}</span></div>
                            <button className="btn btn-sm btn-blue" onClick={()=>selectStudent(s)} style={{width:'100%',justifyContent:'center',marginTop:8}}>
                              <ArrowsRightLeftIcon/>Transfer
                            </button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {selStudent && !done && (
                <div className="panel">
                  <div className="panel-title">Transfer Student</div>

                  {/* Selected student card */}
                  <div className="selected-stu" style={{marginBottom:16}}>
                    <div>
                      <div className="name">{selStudent.user?.name||selStudent.name}</div>
                      <div className="meta">
                        {selStudent.admissionNo} · Roll {selStudent.rollNo} ·
                        Currently in <strong>{fromCls?.name||selClass?.name||'—'} — {selStudent.section||selSection}</strong>
                      </div>
                    </div>
                    <button className="clear" onClick={clearTransfer}>← Back</button>
                  </div>

                  <div style={{fontSize:'.76rem',fontWeight:700,color:'#6b7194',marginBottom:10}}>Transfer To</div>
                  <div className="setup-grid" style={{maxWidth:500,marginBottom:12}}>
                    <div className="fl">
                      <label className="fl-label">Target Class</label>
                      <select className="fl-select" value={toClassId} onChange={e=>{setToClassId(e.target.value);setToSection('');}}>
                        <option value="">Select class…</option>
                        {classes.map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div className="fl">
                      <label className="fl-label">Target Section</label>
                      <select className="fl-select" value={toSection} onChange={e=>setToSection(e.target.value)} disabled={!toClassId}>
                        <option value="">Select section…</option>
                        {toSections.map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="fl">
                      <label className="fl-label">Academic Year</label>
                      <input className="fl-input" value={academicYear} onChange={e=>setAcademicYear(e.target.value)}/>
                    </div>
                  </div>

                  {toClassId&&toSection&&(
                    <div className="arrow-row" style={{justifyContent:'flex-start',padding:'8px 0 12px'}}>
                      <div className="arrow-badge" style={{background:'#fafbff'}}>
                        {fromCls?.name||selClass?.name} — {selStudent.section||selSection}
                      </div>
                      <ArrowRightIcon className="arrow-icon"/>
                      <div className="arrow-badge" style={{background:'#f0fdf4',borderColor:'#a7f3d0',color:'#065f46'}}>
                        {toCls?.name||'—'} — {toSection}
                      </div>
                    </div>
                  )}

                  <div className="fl" style={{maxWidth:500,marginBottom:16}}>
                    <label className="fl-label">Reason</label>
                    <textarea className="fl-textarea" required value={reason} onChange={e=>setReason(e.target.value)} placeholder="e.g. Parent request, class size balancing…"/>
                  </div>

                  <div style={{display:'flex',gap:8}}>
                    <button className="btn btn-ghost" onClick={clearTransfer}>← Cancel</button>
                    <button className="btn btn-green" onClick={handleTransfer} disabled={executing||!toClassId||!toSection}>
                      {executing?<><div className="spinner"/>Processing…</>:readOnly?<><CheckCircleIcon/>Request Transfer</>:<><ArrowsRightLeftIcon/>Execute Transfer</>}
                    </button>
                  </div>
                </div>
              )}

              {done && (
                <div className="panel" style={{textAlign:'center',padding:'2rem'}}>
                  <CheckCircleIcon style={{width:44,height:44,color:done.isRequest?'#3b6ef8':'#10c98e',margin:'0 auto 14px',display:'block'}}/>
                  <div style={{fontSize:'1rem',fontWeight:700,color:'#0f1123',marginBottom:6}}>
                    {done.isRequest?'Request Submitted!':'Transfer Complete!'}
                  </div>
                  <div style={{fontSize:'.8rem',color:'#9094a8',marginBottom:16}}>{done.message}</div>
                  {!done.isRequest&&(
                    <div className="summary-grid" style={{maxWidth:300,margin:'0 auto 16px'}}>
                      <div className="sum-card"><div className="sum-val" style={{color:'#3b6ef8'}}>{done.attendanceMigrated}</div><div className="sum-label">Attendance Migrated</div></div>
                      <div className="sum-card"><div className="sum-val" style={{color:'#10c98e'}}>{done.feesMigrated}</div><div className="sum-label">Fees Updated</div></div>
                    </div>
                  )}
                  <div style={{display:'flex',gap:8,justifyContent:'center'}}>
                    <button className="btn btn-blue" onClick={clearTransfer}>Transfer Another</button>
                    <button className="btn btn-ghost" onClick={()=>setActiveView('history')}><ClockIcon/>View History</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {activeView==='history' && (
        <div className="panel">
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,flexWrap:'wrap',gap:10}}>
            <div className="panel-title" style={{marginBottom:0}}>Transfer History</div>
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              <div className="stu-search" style={{width:220}}>
                <MagnifyingGlassIcon/>
                <input type="text" placeholder="Search…" value={histSearch} onChange={e=>setHistSearch(e.target.value)}/>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={loadHistory}><ArrowPathIcon/>Refresh</button>
            </div>
          </div>
          {loadingHistory ? (
            <div className="cp-loading"><div className="spinner"/>Loading…</div>
          ) : filteredHistory.length===0 ? (
            <div className="cp-empty">No transfer records found</div>
          ) : (
            <>
              <div className="hist-wrap">
                <table className="hist-table">
                  <thead><tr>
                    <th>Date</th><th>Student</th><th>From</th><th>To</th>
                    <th>Att.</th><th>Fees</th><th>Reason</th><th>By</th>
                  </tr></thead>
                  <tbody>
                    {filteredHistory.map(t=>(
                      <tr key={t._id}>
                        <td style={{whiteSpace:'nowrap',color:'#6b7194',fontSize:'.76rem'}}>{fmtDate(t.transferDate||t.createdAt)}</td>
                        <td>
                          <div className="stu-name">{t.student?.user?.name||'—'}</div>
                          <div className="stu-meta">{t.student?.admissionNo}</div>
                        </td>
                        <td><span style={{color:'#6b7194'}}>{t.fromClass?.name||'—'} — {t.fromSection}</span></td>
                        <td><span style={{color:'#3b6ef8',fontWeight:600}}>{t.toClass?.name||'—'} — {t.toSection}</span></td>
                        <td style={{textAlign:'center'}}><span style={{fontFamily:'JetBrains Mono',fontSize:'.8rem',fontWeight:700,color:'#3b6ef8'}}>{t.attendanceMigrated||0}</span></td>
                        <td style={{textAlign:'center'}}><span style={{fontFamily:'JetBrains Mono',fontSize:'.8rem',fontWeight:700,color:'#10c98e'}}>{t.feesMigrated||0}</span></td>
                        <td style={{color:'#6b7194',fontSize:'.76rem'}}>{t.reason||<span style={{color:'#c0c5dc'}}>—</span>}</td>
                        <td>
                          <div style={{fontSize:'.76rem',fontWeight:600}}>{t.transferredBy?.name||'—'}</div>
                          <div style={{fontSize:'.68rem',color:'#9094a8',textTransform:'capitalize'}}>{t.transferredBy?.role||''}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="cp-hist-cards">
                {filteredHistory.map(t=>(
                  <div key={t._id} className="cp-mcard">
                    <div className="cp-mc-top">
                      <div><div className="stu-name">{t.student?.user?.name||'—'}</div><div className="stu-meta">{t.student?.admissionNo}</div></div>
                      <span style={{color:'#6b7194',fontSize:'.72rem',whiteSpace:'nowrap'}}>{fmtDate(t.transferDate||t.createdAt)}</span>
                    </div>
                    <div className="cp-mc-row"><span className="cp-mc-label">From</span><span style={{color:'#6b7194'}}>{t.fromClass?.name||'—'} — {t.fromSection}</span></div>
                    <div className="cp-mc-row"><span className="cp-mc-label">To</span><span style={{color:'#3b6ef8',fontWeight:600}}>{t.toClass?.name||'—'} — {t.toSection}</span></div>
                    <div className="cp-mc-row"><span className="cp-mc-label">Attendance Migrated</span><span style={{fontFamily:'JetBrains Mono',fontWeight:700,color:'#3b6ef8'}}>{t.attendanceMigrated||0}</span></div>
                    <div className="cp-mc-row"><span className="cp-mc-label">Fees Updated</span><span style={{fontFamily:'JetBrains Mono',fontWeight:700,color:'#10c98e'}}>{t.feesMigrated||0}</span></div>
                    <div className="cp-mc-row"><span className="cp-mc-label">Reason</span><span style={{color:'#6b7194'}}>{t.reason||'—'}</span></div>
                    <div className="cp-mc-row">
                      <span className="cp-mc-label">By</span>
                      <span style={{fontWeight:600,textAlign:'right'}}>{t.transferredBy?.name||'—'} <span style={{fontSize:'.68rem',color:'#9094a8',textTransform:'capitalize'}}>({t.transferredBy?.role||''})</span></span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function ClassPromotion({ readOnly=false }) {
  const [activeTab, setActiveTab] = useState('promotion');
  const [classes,   setClasses]   = useState([]);

  useEffect(() => {
    const url = readOnly ? '/coordinator/classes' : '/admin/classes';
    api.get(url).then(r => setClasses(r.data?.classes||r.data||[])).catch(()=>{});
  }, [readOnly]);

  return (
    <div className="cp"><style>{css}</style>
      <div className="cp-tabs">
        <button className={`cp-tab${activeTab==='promotion'?' active':''}`} onClick={()=>setActiveTab('promotion')}>
          <ArrowRightIcon/>Class Promotion
        </button>
        <button className={`cp-tab${activeTab==='transfer'?' active':''}`} onClick={()=>setActiveTab('transfer')}>
          <ArrowsRightLeftIcon/>Section Transfer
        </button>
      </div>

      {activeTab==='promotion' && <PromotionTab classes={classes} readOnly={readOnly}/>}
      {activeTab==='transfer'  && <SectionTransferTab classes={classes} readOnly={readOnly}/>}
    </div>
  );
}